// src/components/navigation/FilterTopBar.js
import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import Slider from "rc-slider";

import "react-datepicker/dist/react-datepicker.css";
import "rc-slider/assets/index.css";

import search_icon_new from "../../assets/images/search_icon_new.svg";
import filter_list_icon from "../../assets/images/filter_list_icon.svg";
import export_icon from "../../assets/images/export_icon.svg";
import calendar_icon from "../../assets/images/calendar_icon.svg";

import { exportEmployeesCSV } from "../../store/slices/userSlice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setPayrollFilters } from "../../store/slices/payrollSlice";
import { formatDateForApi } from "../../utils/dateUtils";

const FilterTopBar = ({
  title = "",
  count = 0,
  search = "",
  showSearch = true,
  onSearchChange = () => { },
  placeholder = "Search",
  showExport = true,
  showFilter = true,
  companyId = null,
  exportCSVLoading = false,
  showDashboardExport = false,
  onFilterChange = () => { },
  handleExportCSV = () => { },
  onDateChange = () => { },
  customExportHandler = null,
  customExportLoading = false,
  disableExport = false,
  startDate = null,
  endDate = null,
  searchClassName = "",
  filterClassName = "",
  exportClassName = "",
  isPayrollPage = false,
}) => {

  const dispatch = useAppDispatch();

  const [dateRange, setDateRange] = useState([startDate, endDate]);
  const [localStartDate, localEndDate] = dateRange;

  const [payRange, setPayRange] = useState([0, 1000000]);

  const [status, setStatus] = useState("active");

  const isFirstRender = useRef(true);

  useEffect(() => {
    setDateRange([startDate, endDate]);
  }, [startDate, endDate]);

  const getStatusValue = () => status === "all" ? null : status === "active" ? true : false;

  useEffect(() => {
    if ((localStartDate && !localEndDate) || (!localStartDate && localEndDate)) {
      return;
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    onFilterChange({
      startDate: formatDateForApi(localStartDate),
      endDate: formatDateForApi(localEndDate),
      payRange,
      status: getStatusValue(),
    });
  }, [localStartDate, localEndDate, payRange, status, onFilterChange]);

  const buildCompanyParams = (extra = {}) => {
    const params = {
      startDate: formatDateForApi(localStartDate),
      endDate: formatDateForApi(localEndDate),
      status: getStatusValue(),
      minPayAvg: payRange[0],
      maxPayAvg: payRange[1],
      ...extra,
    };

    if (search) {
      params.search = search;
    }

    return params;
  };

  return (
    <div className="top_bar d-flex align-items-center mt-3">
      {count != 0 && (
        <h4 className="mb-0">
          {title} ({count})
        </h4>
      )}

      {showSearch && (
        <div className={`search_field d-flex align-items-center mr_10 ${searchClassName || ""}`}>
          <input
            type="text"
            className="form-control"
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <span className="serach_icon">
            <img src={search_icon_new} alt="Search" />
          </span>
        </div>
      )}

      {showFilter && (
        <div className={`dropdown filter ${filterClassName || ""} mr_10`}>
          <button
            className="btn btn_secondary"
            type="button"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
            aria-expanded="false"
          >
            <img src={filter_list_icon} alt="Filter" /> Filter
          </button>

          <div
            className="dropdown-menu p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="fs_12 mb_6">Filter by {isPayrollPage ? "Payroll" : "Creation"} Date</p>
            <div className="datepicker  position-relative">
              <DatePicker
                selectsRange={true}
                startDate={localStartDate}
                endDate={localEndDate}
                onChange={(update) => {
                  setDateRange(update);

                  if (onDateChange) {
                    const [start, end] = update;
                    onDateChange({
                      startDate: start,
                      endDate: end,
                    });
                  }
                }}
                dateFormat="MM/dd/yyyy"
                className="form-control"
                placeholderText="Choose date range"
                shouldCloseOnSelect={false}
              />
              <img
                src={calendar_icon}
                className="datepicker_icon"
                alt="calendar"
              />
            </div>

            <div className="mt_20">
              <div className="d-flex justify-content-between align-items-center mb_10">
                <p className="fs_12 mb-0">Pay Avg Range</p>
                <span
                  className="fs_12 text-primary cursor-pointer"
                  onClick={() => setPayRange([0, 1000000])}
                >
                  Clear
                </span>
              </div>

              <Slider
                range
                min={0}
                max={1000000}
                step={500}
                value={payRange}
                onChange={setPayRange}
                trackStyle={[{ backgroundColor: "#1E6BFF", height: 4 }]}
                handleStyle={[
                  {
                    borderColor: "#1E6BFF",
                    height: 14,
                    width: 14,
                    backgroundColor: "#fff",
                  },
                  {
                    borderColor: "#1E6BFF",
                    height: 14,
                    width: 14,
                    backgroundColor: "#fff",
                  },
                ]}
                railStyle={{ backgroundColor: "#E0E0E0", height: 4 }}
              />

              <div className="d-flex justify-content-between fs_12 mt_6">
                <span>${payRange[0].toLocaleString()}</span>
                <span>${payRange[1].toLocaleString()}</span>
              </div>
            </div>

            <div className="mt_16">
              <p className="fs_12 mb_6">Status</p>
              <select
                className="form-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {showExport && (
        <button
          className={`btn btn_secondary border ml_10 ${exportClassName || ""}`}
          disabled={exportCSVLoading || customExportLoading || (!companyId && !customExportHandler) || disableExport}
          onClick={() => {
            if (!exportCSVLoading && !customExportLoading) {
              if (customExportHandler) {
                customExportHandler();
              } else if (companyId) {
                const params = buildCompanyParams({
                  companyId: companyId
                });
                dispatch(
                  exportEmployeesCSV(params)
                );
              }
            }
          }}
        >
          <img src={export_icon} alt="Export" /> {customExportLoading ? "Exporting" : "Export"}
        </button>
      )}

      {showDashboardExport && (
        <button className="btn btn_secondary border"
          onClick={() => handleExportCSV()}>
          <img src={exportCSVLoading ? null : export_icon} alt="Export" /> {exportCSVLoading ? "Exporting" : "Export"}
        </button>
      )}
    </div>
  );
};

export default FilterTopBar;